import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';
import { ConfirmDialog } from '../ui/confirm-dialog';

const DirtyNavigationContext = createContext(null);

function BeforeUnloadGuard() {
  useBeforeUnload(useCallback((event) => {
    event.preventDefault();
    event.returnValue = '';
  }, []), { capture: true });

  return null;
}

export function DirtyNavigationProvider({ children }) {
  const sourcesRef = useRef(new Map());
  const pendingReleaseRef = useRef(null);
  const [revision, setRevision] = useState(0);
  const [pendingRelease, setPendingRelease] = useState(null);

  const updateSource = useCallback((sourceId, updates) => {
    const current = sourcesRef.current.get(sourceId) || { dirty: false, inFlight: false };
    const next = { ...current, ...updates };
    sourcesRef.current.set(sourceId, next);
    setRevision(value => value + 1);
  }, []);

  const registerSource = useCallback((sourceId, onDiscard) => {
    const current = sourcesRef.current.get(sourceId) || { dirty: false, inFlight: false };
    sourcesRef.current.set(sourceId, { ...current, onDiscard });
    setRevision(value => value + 1);
  }, []);

  const unregisterSource = useCallback((sourceId) => {
    if (pendingReleaseRef.current?.sourceId === sourceId) {
      pendingReleaseRef.current.resolve(false);
      pendingReleaseRef.current = null;
      setPendingRelease(null);
    }
    if (sourcesRef.current.delete(sourceId)) {
      setRevision(value => value + 1);
    }
  }, []);

  const releaseSource = useCallback((sourceId) => {
    updateSource(sourceId, { dirty: false, inFlight: false });
  }, [updateSource]);

  const requestRelease = useCallback((sourceId, description) => {
    const source = sourcesRef.current.get(sourceId);
    if (!source?.dirty && !source?.inFlight) {
      return Promise.resolve(true);
    }
    if (source.inFlight) {
      return Promise.resolve(false);
    }

    pendingReleaseRef.current?.resolve(false);
    return new Promise((resolve) => {
      const request = { sourceId, description, resolve };
      pendingReleaseRef.current = request;
      setPendingRelease(request);
    });
  }, []);

  const hasDirty = Array.from(sourcesRef.current.values()).some(source => source.dirty);
  const hasInFlight = Array.from(sourcesRef.current.values()).some(source => source.inFlight);
  const hasActiveSource = hasDirty || hasInFlight;

  const blocker = useBlocker(useCallback(() => (
    Array.from(sourcesRef.current.values()).some(source => source.dirty || source.inFlight)
  ), []));

  useEffect(() => {
    if (blocker.state === 'blocked' && !hasActiveSource) {
      blocker.reset();
    }
  }, [blocker, hasActiveSource]);

  useEffect(() => {
    if (blocker.state !== 'blocked' || !pendingReleaseRef.current) return;
    pendingReleaseRef.current.resolve(false);
    pendingReleaseRef.current = null;
    setPendingRelease(null);
  }, [blocker.state]);

  useEffect(() => () => {
    pendingReleaseRef.current?.resolve(false);
  }, []);

  const cancelPendingRelease = useCallback(() => {
    pendingReleaseRef.current?.resolve(false);
    pendingReleaseRef.current = null;
    setPendingRelease(null);
  }, []);

  const confirmPendingRelease = useCallback(() => {
    const request = pendingReleaseRef.current;
    if (!request) return;
    releaseSource(request.sourceId);
    request.resolve(true);
    pendingReleaseRef.current = null;
    setPendingRelease(null);
  }, [releaseSource]);

  const stayOnPage = useCallback(() => {
    if (blocker.state === 'blocked') blocker.reset();
  }, [blocker]);

  const discardAndProceed = useCallback(() => {
    if (blocker.state !== 'blocked' || hasInFlight) return;
    sourcesRef.current.forEach((source, sourceId) => {
      if (source.dirty) source.onDiscard?.();
      sourcesRef.current.set(sourceId, { ...source, dirty: false });
    });
    setRevision(value => value + 1);
    blocker.proceed();
  }, [blocker, hasInFlight]);

  const contextValue = useMemo(() => ({
    getSource: sourceId => sourcesRef.current.get(sourceId) || { dirty: false, inFlight: false },
    registerSource,
    updateSource,
    unregisterSource,
    releaseSource,
    requestRelease,
    revision
  }), [registerSource, requestRelease, releaseSource, revision, unregisterSource, updateSource]);

  const navigationBlocked = blocker.state === 'blocked';

  return (
    <DirtyNavigationContext.Provider value={contextValue}>
      {children}
      {hasActiveSource && <BeforeUnloadGuard />}
      <ConfirmDialog
        open={navigationBlocked}
        onCancel={stayOnPage}
        onConfirm={discardAndProceed}
        title={hasInFlight ? 'Save in progress' : 'Discard unsaved changes?'}
        description={hasInFlight
          ? 'Please wait for the current save to finish before leaving this page.'
          : 'Your unsaved changes will be lost if you leave this page.'}
        cancelText="Stay"
        confirmText="Discard and leave"
        variant="destructive"
        loading={hasInFlight}
      />
      <ConfirmDialog
        open={Boolean(pendingRelease) && !navigationBlocked}
        onCancel={cancelPendingRelease}
        onConfirm={confirmPendingRelease}
        title="Discard unsaved changes?"
        description={pendingRelease?.description || 'Your unsaved changes will be lost.'}
        cancelText="Keep editing"
        confirmText="Discard"
        variant="destructive"
      />
    </DirtyNavigationContext.Provider>
  );
}

export function useDirtyNavigationGuard(sourceId, { onDiscard } = {}) {
  const context = useContext(DirtyNavigationContext);
  const generatedId = useId();
  const resolvedId = sourceId || generatedId;
  const activeRef = useRef(false);
  const onDiscardRef = useRef(onDiscard);
  onDiscardRef.current = onDiscard;

  if (!context) {
    throw new Error('useDirtyNavigationGuard must be used within DirtyNavigationProvider');
  }

  const {
    getSource,
    registerSource,
    updateSource,
    unregisterSource,
    releaseSource,
    requestRelease
  } = context;

  useEffect(() => {
    activeRef.current = true;
    registerSource(resolvedId, () => onDiscardRef.current?.());
    return () => {
      activeRef.current = false;
      unregisterSource(resolvedId);
    };
  }, [registerSource, resolvedId, unregisterSource]);

  const setDirty = useCallback((dirty) => {
    if (!activeRef.current) return;
    updateSource(resolvedId, { dirty: Boolean(dirty) });
  }, [resolvedId, updateSource]);

  const setInFlight = useCallback((inFlight) => {
    if (!activeRef.current) return;
    updateSource(resolvedId, { inFlight: Boolean(inFlight) });
  }, [resolvedId, updateSource]);

  const release = useCallback(() => {
    if (!activeRef.current) return;
    releaseSource(resolvedId);
  }, [releaseSource, resolvedId]);

  const confirmAndRelease = useCallback((description) => {
    if (!activeRef.current) return Promise.resolve(false);
    return requestRelease(resolvedId, description);
  }, [requestRelease, resolvedId]);

  const source = getSource(resolvedId);

  return {
    isDirty: source.dirty,
    isInFlight: source.inFlight,
    setDirty,
    setInFlight,
    release,
    confirmAndRelease
  };
}
